import React, { FC, useCallback } from 'react';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types'
import { MarketTemplateMappings } from '@om-fe/core/core/markets/types';
import { BemMethod, createBem } from '@om-fe/utils';
import { BreadcrumbsTournamentModel } from '@om-fe/core/components/breadcrumbs/breadcrumbs.constants';
import { useRPCTournamentCall } from '@om-fe/core/components/breadcrumbs/breadcrumbs.utils';
import { Breadcrumbs } from '../../components/breadcrumbs/breadcrumbs';
import { DisciplineRouteType } from '@om-fe/core/components/discipline-anchor/discipline-route.utils';
import MarketTemplateMappingsContext from '@om-fe/core/components/market-template-mappings-context/marketTemplateMappingsContext';
import UserBehaviorTrackingContext from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking-context';
import { RouteNames } from '@om-fe/core/core/routing/utils';
import { UserBehaviorTrackingLocation } from '@om-fe/core/components/user-behavior-tracking-context/user-behavior-tracking.utils';
import TournamentOutrights from '@om-fe/core/components/tournament-outrights/tournament-outrights';
import { isRacingDiscipline } from '@om-fe/core/components/sport-parent/sport-parent.utils';
import { FlagIconLocations } from '@om-fe/widgets/dist/components/icon/flag-icon';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';

interface LiveEventOutrightsPublicProps {
    readonly tournamentId: string;
    readonly disciplineId: string;
    readonly marketTemplateMappings: MarketTemplateMappings;
    readonly showEventCategory?: boolean;
}

interface LiveEventOutrightsStoreProps {
    readonly isLarge: boolean;
}

type LiveEventOutrightsProps = LiveEventOutrightsPublicProps &
    LiveEventOutrightsStoreProps;

const bem: BemMethod = createBem('MixedGroupedOutrights');

const LiveEventOutrightsComponent = ({
    tournamentId,
    disciplineId,
    marketTemplateMappings,
    isLarge,
    showEventCategory,
}: LiveEventOutrightsProps) => {
    const tournamentData: BreadcrumbsTournamentModel | undefined = useRPCTournamentCall(tournamentId);
    const config: OMFE2ConfigModel = getConfig();
    const showExpand: boolean | undefined = config.matchListConfig?.outrights?.showExpand;
    const showFavoriteMarket: boolean | undefined = config.matchListConfig?.outrights?.showFavoriteMarket;
    
    const getBreadcrumbs = useCallback(() => {
        if (!tournamentData) {
            return null;
        }

        const {
            sportId,
            categoryId,
            id,
            templateId,
            venueId,
        } = tournamentData;

        return (
            <Breadcrumbs 
                isLarge={isLarge}
                disciplineId={sportId}
                tournamentId={id}
                routeType={showEventCategory ? DisciplineRouteType.Category : DisciplineRouteType.Location}
                categoryId={categoryId}
                eventId={templateId}
                isLive={true}
                locationId={isRacingDiscipline(sportId) ? FlagIconLocations.World : venueId}
            />
        );
    }, [tournamentData]);

    return (
        <>
            {isLarge && getBreadcrumbs()}
            <MarketTemplateMappingsContext.Provider value={marketTemplateMappings}>
                <UserBehaviorTrackingContext.Provider 
                    value={{
                        userBehaviorTrackingPage: RouteNames.event,
                        userBehaviorTrackingLocation: UserBehaviorTrackingLocation.MatchDetails
                    }}
                >
                    <div className={bem()}>
                        <TournamentOutrights
                            tournamentId={tournamentId}
                            disciplineId={disciplineId}
                            showExpand={showExpand} 
                            showFavoriteMarket={showFavoriteMarket}
                            columnByBreakpoint={true}
                        />
                    </div>
                </UserBehaviorTrackingContext.Provider>
            </MarketTemplateMappingsContext.Provider>
        </>
    );
}

export const LiveEventOutrights: FC<LiveEventOutrightsPublicProps> = connect<LiveEventOutrightsStoreProps, LiveEventOutrightsProps, AppState>(
    (state: AppState) => {
        return {
            isLarge: state.breakpoints.large || state.breakpoints.xl,
        }
    }
)(LiveEventOutrightsComponent);
export default LiveEventOutrights;
